import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  VStack,
  HStack,
  Text,
  Spinner,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Icon,
  useDisclosure,
} from "@chakra-ui/react";
import { DarwinTool } from "build-ts/darwin_agent";
import { JinjaEnv } from "build-ts/jinja";
import { DarwinAgentType } from "build-ts/common";
import { getSystemPromptDocumentation, getToolById, updateTool } from "services/NewDarwinEvaServices";
import JinjaHighlightedTextarea from "components/common/JinjaHighlightedTextarea";
import showToast from "hooks/useCustomToast";
import DocModal from "components/new_darwin_eva/DocModal";
import { FiX } from "react-icons/fi";
import { FaBookOpen } from "react-icons/fa";

const EvaToolDetailPage: React.FC = () => {
  const { tool_id } = useParams<{ tool_id: string }>();
  const navigate = useNavigate();

  const [tool, setTool] = useState<DarwinTool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [availableVariables, setAvailableVariables] = useState<JinjaEnv | null>(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const { isOpen: isDocsOpen, onOpen: onDocsOpen, onClose: onDocsClose } = useDisclosure();

  const [formState, setFormState] = useState<{
    name: string;
    description: string;
    agentic_name: string;
    agentic_description: string;
    template: string;
  }>({
    name: "",
    description: "",
    agentic_name: "",
    agentic_description: "",
    template: "",
  });

  useEffect(() => {
    const loadTool = async () => {
      if (!tool_id) {
        showToast("No tool ID provided", "", "error");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getToolById(tool_id);
        if (!data) {
          showToast("Failed to load tool data", "", "error");
          return;
        }
        const loadedTool = DarwinTool.fromJSON(data);
        setTool(loadedTool);
        setFormState({
          name: loadedTool.header?.name || "",
          description: loadedTool.header?.description || "",
          agentic_name: loadedTool.agentic_name || "",
          agentic_description: loadedTool.agentic_description || "",
          template: (loadedTool as any).template || "",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadTool();
  }, [tool_id]);

  const handleFieldChange = (field: keyof typeof formState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    if (!tool) {
      return;
    }
    setFormState({
      name: tool.header?.name || "",
      description: tool.header?.description || "",
      agentic_name: tool.agentic_name || "",
      agentic_description: tool.agentic_description || "",
      template: (tool as any).template || "",
    });
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!tool || !tool.header?.id) {
      return;
    }
    setIsSaving(true);
    try {
      const updated: DarwinTool = {
        ...tool,
        header: {
          ...tool.header,
          name: formState.name,
          description: formState.description,
        },
        agentic_name: formState.agentic_name,
        agentic_description: formState.agentic_description,
        ...(formState.template !== undefined ? { template: formState.template } : {}),
      } as any;

      const response = await updateTool(tool.header.id, updated);
      if (response) {
        const savedTool = DarwinTool.fromJSON(response);
        setTool(savedTool);
        setFormState({
          name: savedTool.header?.name || "",
          description: savedTool.header?.description || "",
          agentic_name: savedTool.agentic_name || "",
          agentic_description: savedTool.agentic_description || "",
          template: (savedTool as any).template || "",
        });
        setIsEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const fetchSystemPromptDocumentation = async () => {
    try {
      setIsLoadingDoc(true);
      const data = await getSystemPromptDocumentation();
      setAvailableVariables(data || null);
    } catch (error) {
      console.error("Error fetching system prompt documentation:", error);
      setAvailableVariables(null);
    } finally {
      setIsLoadingDoc(false);
    }
  };

  const handleViewDocs = async () => {
    onDocsOpen();
    await fetchSystemPromptDocumentation();
  };

  if (isLoading) {
    return (
      <Box
        className="eva-container eva-small-scrollbar"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="50vh"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading tool data...</Text>
        </VStack>
      </Box>
    );
  }

  if (!tool) {
    return (
      <Box p={4}>
        <Text>Tool not found.</Text>
        <Button mt={4} onClick={() => navigate("/eva/tools")} variant="outline">
          Back to tools
        </Button>
      </Box>
    );
  }

  return (
    <Box bg="gray.100" p={2} height="100vh" overflow="hidden">
      <Grid
        height="100%"
        width="100%"
        templateColumns="minmax(0, 40%) minmax(0, 60%)"
        gap={2}
      >
        {/* Left Panel - Tool Info (similar to modal content) */}
        <Box
          bg="white"
          px={4}
          py={4}
          borderRadius="12px"
          boxShadow="lg"
          display="flex"
          flexDirection="column"
          overflowY="auto"
          minHeight={0}
          css={{
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E0",
              borderRadius: "2px",
            },
          }}
        >
          <HStack justify="space-between" align="center" mb={3}>
            <Text fontSize="lg" color="purple.700" fontWeight="700" mb={0}>
              Tool Details
            </Text>
            <HStack>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Icon as={FiX} />}
                onClick={() => navigate("/eva/tools")}
                iconSpacing={0}
              >
              </Button>
              {!isEditing ? (
                <Button
                  size="sm"
                  colorScheme="purple"
                  bg="purple.700"
                  color="white"
                  _hover={{ bg: "purple.600" }}
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={handleSave}
                    isLoading={isSaving}
                  >
                    Save
                  </Button>
                </>
              )}
            </HStack>
          </HStack>

          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Internal Name
              </FormLabel>
              <Input
                size="sm"
                value={formState.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                isDisabled={!isEditing}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Description
              </FormLabel>
              <Textarea
                value={formState.description}
                onChange={(e) => handleFieldChange("description", e.target.value)}
                rows={4}
                resize="none"
                isDisabled={!isEditing}
              />
            </FormControl>

            <Box
              bg="blue.50"
              borderColor="blue.400"
              borderWidth="2px"
              borderRadius="md"
              p={4}
            >
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700" color="blue.700">
                    Name to Agent
                  </FormLabel>
                  <Input
                    size="md"
                    value={formState.agentic_name}
                    onChange={(e) =>
                      handleFieldChange("agentic_name", e.target.value)
                    }
                    bg="white"
                    fontSize="md"
                    fontWeight="600"
                    isDisabled={!isEditing}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="700" color="blue.700">
                    Description to Agent
                  </FormLabel>
                  <Textarea
                    value={formState.agentic_description}
                    onChange={(e) =>
                      handleFieldChange("agentic_description", e.target.value)
                    }
                    rows={8}
                    resize="none"
                    bg="white"
                    fontSize="md"
                    fontWeight="600"
                    isDisabled={!isEditing}
                  />
                </FormControl>
              </VStack>
            </Box>

            <HStack>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  Tool Type
                </FormLabel>
                <Input
                  size="sm"
                  value={tool.header?.type || ""}
                  isReadOnly
                  bg="gray.50"
                  cursor="not-allowed"
                  color="gray.600"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  Is Response Tool
                </FormLabel>
                <Input
                  size="sm"
                  value={tool.header?.is_response_tool ? "True" : "False"}
                  isReadOnly
                  bg="gray.50"
                  cursor="not-allowed"
                  color="gray.600"
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Args Schema JSON
              </FormLabel>
              <Box
                bg="gray.900"
                color="green.300"
                borderRadius="md"
                p={4}
                overflow="auto"
                fontFamily="monospace"
                fontSize="sm"
                textAlign="left"
                whiteSpace="pre"
                maxH="40vh"
                minH="200px"
              >
                {tool.args_schema
                  ? JSON.stringify(JSON.parse(tool.args_schema), null, 2)
                  : "No schema available"}
              </Box>
            </FormControl>
          </VStack>
        </Box>

        {/* Right Panel - Template Editor */}
        <Box
          bg="white"
          px={4}
          py={4}
          borderRadius="12px"
          boxShadow="lg"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <HStack justify="space-between" align="center" mb={3}>
            <Text fontSize="lg" color="purple.700" fontWeight="700" mb={0}>
              Skill Template
            </Text>
            <Button
              leftIcon={<FaBookOpen />}
              size="sm"
              variant="solid"
              onClick={handleViewDocs}
              colorScheme="purple"
              bg="purple.700"
              color="white"
              _hover={{ borderColor: "purple.600", bg: "purple.600" }}
              fontWeight="700"
            >
              Jinja Docs
            </Button>
          </HStack>

          <Box flex="1" overflow="auto">
            <JinjaHighlightedTextarea
              value={formState.template}
              onChange={(value) => handleFieldChange("template", value)}
              placeholder="Enter Jinja template for this skill tool..."
              isReadOnly={!isEditing}
            />
          </Box>
        </Box>
      </Grid>

      <DocModal
        isDocsOpen={isDocsOpen}
        onDocsClose={onDocsClose}
        isLoadingDoc={isLoadingDoc}
        availableVariables={availableVariables}
        agentType={DarwinAgentType.ANALYST_CHAT_AGENT}
      />
    </Box>
  );
};

export default EvaToolDetailPage;

